import { useEffect, useState } from 'react'
import { Box, HStack, Icon, Text, VStack } from '@chakra-ui/react'
import { LuCheck } from 'react-icons/lu'
import { getOrderHistory } from '@/api/orders.api'
import type { OrderHistory, OrderStatus } from '@/common/order.common'

interface Props {
  orderId: number
  /** SIB who placed the order — used for the "… hat aufgegeben" line. */
  sibName: string
  /** Leistungsempfänger / bank shown in the submission line. */
  bankName: string
  /** Order creation timestamp, for the submission line. */
  createdAt: string
}

type TimelineEntry = {
  key: string
  text: string
  timestamp: string
}

const STATUS_LABEL: Record<string, string> = {
  SUBMITTED: 'Offen',
  CORRECTED: 'Korrektur',
  APPROVED: 'Freigegeben',
  REJECTED: 'Abgelehnt',
  COMPLETED: 'Abgeschlossen',
  DELETED: 'Gelöscht',
}

function statusLabel(status: OrderStatus | string) {
  return STATUS_LABEL[status] ?? status
}

function formatTimestamp(iso?: string) {
  if (!iso) return ''
  const d = new Date(iso)
  // Intl.DateTimeFormat.format throws on an invalid date — guard first.
  if (Number.isNaN(d.getTime())) return ''
  const date = new Intl.DateTimeFormat('de-DE').format(d)
  const time = new Intl.DateTimeFormat('de-DE', {
    hour: 'numeric',
    minute: '2-digit',
  }).format(d)
  return `${date} | ${time} Uhr`
}

export function OrderHistoryTimeline({ orderId, sibName, bankName, createdAt }: Props) {
  const [history, setHistory] = useState<OrderHistory[]>([])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const data = await getOrderHistory(orderId)
        if (!cancelled) setHistory(Array.isArray(data) ? data : [])
      } catch {
        if (!cancelled) setHistory([])
      }
    })()
    return () => {
      cancelled = true
    }
  }, [orderId])

  // Newest transitions first, the original submission last (matches the mockup).
  const transitions: TimelineEntry[] = [...history]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .map(h => ({
      key: `h-${h.id}`,
      text: `Status von ${statusLabel(h.fromStatus as OrderStatus)} zu ${statusLabel(
        h.toStatus as OrderStatus
      )} geändert`,
      timestamp: formatTimestamp(h.createdAt),
    }))

  const entries: TimelineEntry[] = [
    ...transitions,
    {
      key: 'submission',
      text: `${sibName} hat eine Bestellung für ${bankName} aufgegeben`,
      timestamp: formatTimestamp(createdAt),
    },
  ]

  return (
    <VStack align="stretch" gap={3}>
      {entries.map(entry => (
        <HStack key={entry.key} align="start" gap={3}>
          <Box
            mt={0.5}
            boxSize="5"
            borderRadius="full"
            bg="green.subtle"
            color="green.fg"
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
          >
            <Icon as={LuCheck} boxSize="3" />
          </Box>
          <VStack align="start" gap={0}>
            <Text fontSize="sm" color="fg.default">
              {entry.text}
            </Text>
            {entry.timestamp && (
              <Text fontSize="xs" color="fg.muted">
                {entry.timestamp}
              </Text>
            )}
          </VStack>
        </HStack>
      ))}
    </VStack>
  )
}
